import { forwardRef, type ComponentPropsWithoutRef, type ElementType } from 'react';

import { cx } from '../internal/cx.js';
import { buildSpacingStyle, pickSpacingProps, type SpacingProps } from '../internal/spacing.js';

export type SectionSize = '1' | '2' | '3' | '4';

export type SectionProps = ComponentPropsWithoutRef<'section'> &
  SpacingProps & {
    /** Polymorphic element tag. */
    as?: ElementType;
    /** Vertical padding preset. */
    size?: SectionSize;
  };

/** Vertical section wrapper with padding presets. Requires `@import '@nrx/ui/theme.css'`. */
export const Section = forwardRef<HTMLElement, SectionProps>(function Section(
  {
    as,
    className,
    style,
    size = '2',
    p,
    px,
    py,
    pt,
    pr,
    pb,
    pl,
    m,
    mx,
    my,
    mt,
    mr,
    mb,
    ml,
    ...rest
  },
  ref,
): React.JSX.Element {
  const Component = as ?? 'section';
  const spacingStyle = buildSpacingStyle(
    pickSpacingProps({ p, px, py, pt, pr, pb, pl, m, mx, my, mt, mr, mb, ml }),
  );

  return (
    <Component
      ref={ref as never}
      className={cx('nrx-section', `nrx-section--${size}`, className)}
      style={{ ...spacingStyle, ...style }}
      {...rest}
    />
  );
});
